class CustomCursor extends HTMLElement {
  constructor() {
    super();
    this.cursorElement = document.createElement('div');
    this.cursorColors = ["#ebff5a", "#dc8dff", "#02d4a6", "#826aed", "#99f8f8"];
    this.cursorColorsIndex = 0;
    this.cursorTextElement = document.createElement('div');
    this.cursorTextElement.classList.add("cursor-text");
    this.cursorDotElement = document.createElement('div');
    this.cursorDotElement.classList.add("cursor-dot");
    this.cursorDotElement.setAttribute('part', 'cursor-dot'); // Enable external styling with ::part
    this.cursorDotBlurElement = document.createElement('div');
    this.cursorDotBlurElement.classList.add("cursor-dot-blur");
    this.attachShadow({ mode: 'open' });
    this.cursorDotElement.appendChild(this.cursorDotBlurElement);
    this.cursorElement.appendChild(this.cursorDotElement);
    this.cursorElement.appendChild(this.cursorTextElement);
    this.shadowRoot.appendChild(this.cursorElement);

    this.cursorElement.classList.add('cursor');
    this.posX = 0;
    this.posY = 0;
    this.clicked = false;
    this.hidden = false;
    this.cursorText = 'See more';
    this.isHoveringCursorElement = false;

    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._onMouseOver = this._onMouseOver.bind(this);
    this._onMouseOut = this._onMouseOut.bind(this);
    this._onMouseScroll = this._onMouseScroll.bind(this);
  }

  connectedCallback() {
    this._applyStyles();
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mouseup', this._onMouseUp);
    window.addEventListener('mouseenter', this._onMouseEnter);
    window.addEventListener('mouseleave', this._onMouseLeave);
    window.addEventListener('mouseover', this._onMouseOver);
    window.addEventListener('mouseout', this._onMouseOut);
    window.addEventListener('scroll', this._onMouseScroll);
  }

  disconnectedCallback() {
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('mouseenter', this._onMouseEnter);
    window.removeEventListener('mouseleave', this._onMouseLeave);
    window.removeEventListener('mouseover', this._onMouseOver);
    window.removeEventListener('mouseout', this._onMouseOut);
    window.removeEventListener('scroll', this._onMouseScroll);
  }

  _applyStyles() {
    const styles = `
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        pointer-events: none;
        z-index: 999999;
      }

      :host(.no-blend-mode) {
        mix-blend-mode: normal;
      }

      .cursor {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        margin-top: 30px;
        margin-left: 30px;
        border-radius: 50%;
        transform: translate(-50%, -50%);
        transition: transform 0.05s, opacity 0.3s, border-color 0.2s;
        will-change: transform, border-color;
        opacity: 0;
      }

      .cursor-dot {
        position: absolute;
        width: 28px;
        height: 28px;
        border: 1px solid #1e1e1e;
        border-radius: 50%;
        transition: transform .3s, border-color 0.2s;
      }

      .cursor-text {
          position: absolute;
          top: calc(50% + 30px);
          left: -19px;
          right: 0;
          margin: 0 auto;
          transform: translateY(-50%);
          font-size: 10px;
          width: 58px;
          height: 58px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: transparent;
          font-family: Acumin, sans-serif;
          font-size: 12px;
          font-weight: normal;
          text-transform: uppercase;
          line-height: 1.2;            
          transition: .3s;
      }

      .hidden {
        opacity: 0;
      }

      .hovering-link .cursor-dot {
        /* transform: translate(10px, 30px) scale(3);
        border: 1px solid #DEFC52; */
      }

      .hovering-link .cursor-dot-blur {
        content: "";
        display: inline-block;
        position: absolute; 
        left: 8px;
        top: 8px;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        filter: blur(8px);
        background-color: ${this.cursorColors[this.cursorColorsIndex]};
        opacity: 0;
        transition: .5s opacity;
      }

      .hovering-link .cursor-dot-blur {
        opacity: 1;
      }

      .hovering-link .cursor-text {
          color: #000;
      }

      /* Enable mix-blend-mode styling via ::part */
      ::part(cursor-dot) {
        mix-blend-mode: difference;
      }

      ${navigator.maxTouchPoints > 0 && `
          .cursor {
            display: none !important;
          }
      `}
    `;

    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    this.shadowRoot.appendChild(styleElement);
  }

  _onMouseMove(e) {

    this.posX = e.clientX;
    this.posY = e.clientY;

    // Adjust for offset
    const offsetX = -40; // Adjust this value to match your cursor offset
    const offsetY = -20;

    const hoverX = e.clientX - offsetX;
    const hoverY = e.clientY - offsetY;

    const isImage = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'IMG');
    const isA = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'A');
    const isButton = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'BUTTON') || e.target.closest('.button-wrap');
    const isH1 = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'H1');
    const isH2 = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'H2');
    const isH3 = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'H3');
    const isH4 = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'H4');
    const isH5 = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'H5');
    const isP = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'P');
    const isUL = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'UL');
    const isOL = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'OL');
    const isLI = document.elementsFromPoint(hoverX, hoverY).some(el => el.tagName === 'LI');       

    setTimeout(() => {
      this.cursorElement.style.opacity = 1;
    }, 50);
    this.cursorElement.style.transform = `translate(${this.posX}px, ${this.posY}px)`;
    const cursorTextEl = e.target.hasAttribute("data-cursor-html");
    const link = e.target.closest('a');
    const button = e.target.closest('.button-wrap');
    if (cursorTextEl) {
      if((link && !link.hasAttribute("disable-hover")) || button && !button.hasAttribute("disable-hover")){
        this.cursorText = e.target.hasAttribute("data-cursor-html") ? e.target.getAttribute("data-cursor-html") : "See more";
        // this.cursorTextElement.innerHTML = cleanHTML(this.cursorText);
      }
    }      
    if (isA || isButton) {
      if(link && !link.hasAttribute("disable-hover")){
        this.classList.add('no-blend-mode');
      }
    } else {
      this.classList.remove('no-blend-mode');
    }
    if(this.cursorElement.classList.contains('hovering-link')){
      if(link && !link.hasAttribute("disable-hover")){
        this.classList.add('no-blend-mode');
      }
    }
  }

  _onMouseDown(e) {
    const link = e.target.closest('a');
    const button = e.target.closest('.button-wrap');
    this.cursorElement.classList.add('clicked');
    const cursorTextEl = e.target.hasAttribute("data-cursor-html");
    if (cursorTextEl) {
      if((link && !link.hasAttribute("disable-hover")) || button && !button.hasAttribute("disable-hover")){
        setTimeout(() => {
          this.cursorText = e.target.hasAttribute("data-cursor-html") ? e.target.getAttribute("data-cursor-html") : "";
          // this.cursorTextElement.innerHTML = cleanHTML(this.cursorText);
        }, 50);
      }
    }    
  }

  _onMouseUp(e) {
    this.cursorElement.classList.remove('clicked');
  }
 
  _onMouseEnter(e) {
    this.cursorElement.classList.remove('hidden');
    // const isA = e.target.tagName === 'A';
    // console.log(e)
    // if (isA) {
    //   this.cursorColorsIndex = Math.floor(Math.random() * this.cursorColors.length);
    //   this.cursorDotBlurElement.style.backgroundColor = `${this.cursorColors[this.cursorColorsIndex]}`;
    // }
  } 

  _onMouseLeave() {
    this.cursorElement.classList.add('hidden');
  }

  _onMouseOver(e) {
    const link = e.target.closest('a');
    const button = e.target.closest('.button-wrap');
    const cursorHoverElement = e.target.closest('[data-cursor-hover]');
    const cursorTextEl = e.target.hasAttribute("data-cursor-html");
    const submitButton = e.target.type === "submit"; 
    const submitButtonContent = e.target.closest("[type='submit']")
    const isA = e.target.tagName === 'A';
    if (isA || cursorTextEl || link || button || submitButton || submitButtonContent) {
      if((link && !link.hasAttribute("disable-hover")) || button && !button.hasAttribute("disable-hover")){
        this.cursorText = e.target.hasAttribute("data-cursor-html") ? e.target.getAttribute("data-cursor-html") : "";
        this.cursorElement.classList.add('hovering-link');
        // this.cursorTextElement.innerHTML = cleanHTML(this.cursorText);
      }
    }
    
    // Handle data-cursor-hover attribute (similar to link hover)
    if(cursorHoverElement && !cursorHoverElement.hasAttribute("disable-hover")){
      // Only randomize color when first entering the element (not already hovering)
      if(!this.isHoveringCursorElement){
        this.cursorColorsIndex = Math.floor(Math.random() * this.cursorColors.length);
        this.isHoveringCursorElement = true;
      }
      this.cursorElement.classList.add('hovering-link');
      this.cursorDotBlurElement.style.backgroundColor = `${this.cursorColors[this.cursorColorsIndex]}`;
      this.classList.add('no-blend-mode');
    }

    if(!link && !button && !cursorHoverElement){
      this.cursorColorsIndex = Math.floor(Math.random() * this.cursorColors.length);
    }
    
    if(this.cursorElement.classList.contains('hovering-link')){
      if(link && !link.hasAttribute("disable-hover")){
        this.classList.add('no-blend-mode');
        // Set random color for links as well
        this.cursorColorsIndex = Math.floor(Math.random() * this.cursorColors.length);
        this.cursorDotBlurElement.style.backgroundColor = `${this.cursorColors[this.cursorColorsIndex]}`;
      }
    }
  }

  _onMouseOut(e) {
    const link = e.target.closest('a');
    const button = e.target.closest('.button-wrap');
    const cursorHoverElement = e.target.closest('[data-cursor-hover]');
    const cursorTextEl = e.target.hasAttribute("data-cursor-html");
    const submitButton = e.target.type === "submit";
    const submitButtonContent = e.target.closest("[type='submit']")
    const isA = e.target.tagName === 'A';

    if (isA || cursorTextEl || link || button || submitButton || submitButtonContent) {
      this.cursorText = "";
      this.cursorElement.classList.remove('hovering-link');
    }
    // Handle data-cursor-hover mouse out
    // Only reset if we're actually leaving the element (relatedTarget is not within the same element)
    if (cursorHoverElement && !cursorHoverElement.hasAttribute("disable-hover")) {
      const relatedTarget = e.relatedTarget;
      // Check if the mouse is moving to a target that's still within the same cursor hover element
      const isMovingWithinElement = relatedTarget && cursorHoverElement.contains(relatedTarget);
      
      if (!isMovingWithinElement) {
        // Actually leaving the element, reset everything
        this.cursorElement.classList.remove('hovering-link');
        this.isHoveringCursorElement = false;
      }
    }
    // this.cursorTextElement.innerHTML = cleanHTML(this.cursorText);
  }

  _onMouseScroll(e) {
    // this.classList.add('no-blend-mode');
  }
 
  setCursorText(text) {
    this.cursorElement.classList.add('hovering-link');
    this.cursorText = text;
    // this.cursorTextElement.innerHTML = cleanHTML(text);
  }

}

if (!window.customElements.get('custom-cursor')) {
  customElements.define('custom-cursor', CustomCursor);
}
